import { FormEvent, useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import useLogout from '@/hooks/useLogout/useLogout';
import { useUpdateAccount } from '@/hooks/useUpdateAccount';
import { queryKeys } from '@/lib/queryKeys';
import { useAuth } from '@/providers/AuthProvider/AuthProvider';
import { Account, userData } from '@/types/types';

export const useAccountSettingsAdapter = (userData: userData) => {
  const queryClient = useQueryClient();
  const [opened, setOpened] = useState(false);
  const [formState, setFormState] = useState<Partial<Account>>({
    name: userData?.name || '',
    email: userData?.email || '',
  });
  const [file, setFile] = useState<File | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const infoMutation = useUpdateAccount(userData.id);
  const photoMutation = useUpdateAccount(userData.id);
  const logoutMutation = useLogout();
  const { logout } = useAuth();

  useEffect(() => {
    if (infoMutation.isSuccess) {
      setShowSuccess(true);
      const timer = setTimeout(() => setShowSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [infoMutation.isSuccess]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (newFile: File | null) => {
    if (!newFile) return;
    setFile(newFile);
    photoMutation.mutate(
      { file: newFile },
      {
        onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.userData._def }),
      }
    );
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { file: _ignored, ...rest } = formState as Partial<Account> & {
      file?: unknown;
    };
    const payload = { ...rest, ...(file && { file }) };
    infoMutation.mutate(payload, {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.userData._def }),
    });
  };

  const handleLogout = () => {
    logoutMutation.mutate(undefined, { onSuccess: () => logout() });
  };

  return {
    userData,
    opened,
    onOpen: () => setOpened(true),
    onClose: () => setOpened(false),
    formState,
    handleChange,
    handleFileChange,
    handleSubmit,
    handleLogout,
    isSaving: infoMutation.isPending,
    isPhotoLoading: photoMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    showSuccess,
    hasError: infoMutation.isError,
  };
};
